import { AxiosInstance, AxiosResponse } from "axios"
import JSZip from "jszip"
import { fileToBase64 } from "utils/files/fileToBase64"
import { errMsgOf } from "utils/errMsgOf"
import type { EXPORT_VARIABLE_TYPES, VARIABLE_STYLE } from "types/engineExport.types"
import type { DATAFILE_TYPE, DatafileSizeOption } from "types/dataImport.types"
import type { RcFile } from "antd/es/upload/interface"

/**
 * Datafile API functions - Refactored to use authenticated axios instance
 * Authentication headers are handled automatically by the axios instance
 */

/**
 * Uploads data for data reporting.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param owner - The owner of the project.
 * @param type - The type of the file to be uploaded. (csv, map or json)
 * @param file - The file object to be uploaded.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function datafileUpload(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  type: DATAFILE_TYPE,
  file: File | Blob | RcFile
): Promise<AxiosResponse<unknown>> {
  // Initialize JSZip instance
  const zip = new JSZip()

  // Add file content to zip
  zip.file(type, file, { compression: "DEFLATE" })

  const zippedContent: Blob = await zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
  })

  const zippedBase64: string = await fileToBase64(zippedContent)

  const encoder = new TextEncoder()
  const encoded: Uint8Array = encoder.encode(zippedBase64)

  const response = await axiosInstance.post(
    `/datafile-save?id=${id}&hint_owner=${owner}&subtype=${type}&encode=base64_zip`,
    encoded
  )
  return response
}
/**
 * Retrieves the list of uploaded data files for a given project
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param owner - The owner of the project.
 * @param type - The type of the file to be retrieved. (csv, map or json)
 * @param size - optional, write 'size' to get the size of the file as the response (for checking if the file exists)
 * @returns The response from the API containing the list of uploaded data files.
 * @throws Will throw an error if the request fails.
 */
export async function datafileGet(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  type: DATAFILE_TYPE,
  size?: DatafileSizeOption
): Promise<AxiosResponse<unknown>> {
  const sizeParam = size ? `&${size}` : ""
  const response = await axiosInstance.get(
    `/datafile-get?id=${id}&hint_owner=${owner}&subtype=${type}&encode=base64${sizeParam}`
  )
  return response
}

/**
 * Reads a project's data CSV or map CSV as plain text (for downloading it).
 *
 * The Conductor answers 200 with the Repos body even when the file is missing
 * or refused, so an `{"Err msg": ...}` body is turned into an error here
 * rather than handed back as the file's text.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param subtype - The file to read: "csv" (data) or "map".
 * @returns The file content as text.
 * @throws An Error with the server message when the answer is an `{"Err msg": ...}` body,
 *   or the axios error when the request fails.
 */
export async function datafileText(
  axiosInstance: AxiosInstance,
  id: string,
  subtype: "csv" | "map"
): Promise<string> {
  const res = await axiosInstance.get(
    `/datafile-get?id=${encodeURIComponent(id)}&subtype=${subtype}&encode=text`,
    { responseType: "text" }
  )
  const msg = errMsgOf(res.data)
  if (msg !== null) throw new Error(msg)
  return String(res.data ?? "")
}

/**
 * Deletes an uploaded data file for a given project ID.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param owner - The owner of the project.
 * @param type - The type of the file to be deleted. (csv, map or json)
 * @returns The response from the API confirming the deletion.
 * @throws Will throw an error if the request fails.
 */
export async function datafileDelete(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  type: DATAFILE_TYPE
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.delete(
    `/datafile-delete?id=${id}&hint_owner=${owner}&subtype=${type}`
  )
  return response
}

/**
 * Combines uploaded data with a data map for a given project ID. It always
 * rebuilds the DQD, so it replaces one that exists (`overwrite`): without
 * it the save of a second import is refused.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @returns The response from the API confirming the combination.
 * @throws Will throw an error if the request fails.
 */
export async function datafileCombine(
  axiosInstance: AxiosInstance,
  id: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(`/datafile-combine?id=${id}&overwrite`)
  return response
}

/**
 * Generates a DQD file from an uploaded JSON data file. It always rebuilds
 * the DQD, so it replaces one that exists (`overwrite`).
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @returns The response from the API confirming the generation.
 * @throws Will throw an error if the request fails.
 */
export async function datafileGenerateDQD(
  axiosInstance: AxiosInstance,
  id: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(`/datafile-generate-dqd?id=${id}&overwrite`)
  return response
}

/**
 * Generates a map file from the questionnaire directly for the current project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param groupSections - Indicates whether to group sections in the map.
 * @param variableNames - The type of variable names to download.
 * @param variableStyle - The variable label style (default vs decipher).
 * @param includeQuestionText - When true, adds a QUESTION_TEXT column with full question wording.
 * @param overwrite - When true, the new map replaces the project's map (the `overwrite` flag);
 *   without it, saving over an existing map is refused.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function generateDatamap(
  axiosInstance: AxiosInstance,
  id: string,
  groupSections?: boolean,
  variableNames?: EXPORT_VARIABLE_TYPES,
  variableStyle?: VARIABLE_STYLE,
  includeQuestionText?: boolean,
  overwrite?: boolean
): Promise<AxiosResponse<unknown>> {
  const config: {
    params: Record<string, string>
  } = {
    params: {},
  }

  if (groupSections) {
    config.params.group_sections = ""
  }

  if (variableNames) {
    config.params.var = variableNames
  }

  // A flag like group_sections: "&overwrite=" with no value reads as set.
  if (overwrite) {
    config.params.overwrite = ""
  }

  // Build request body with options carried server-side
  const body: Record<string, unknown> = { id }
  const options: Record<string, unknown> = {}
  if (variableStyle && variableStyle !== "default") {
    options["label-style"] = variableStyle
  }
  if (includeQuestionText) {
    options["question-text"] = true
  }
  if (Object.keys(options).length > 0) {
    body.options = options
  }

  const response = await axiosInstance.post(`/save-datamap`, body, config)
  return response
}
